import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { fetchRockets } from "../data/rocketFamilies.js";
import {
  getPhotoLocation,
  numberWithCommas,
  setAllValuesTrue,
  toTitleCase,
  useDebounce,
  versionFormat,
} from "../utils.js";
import { fetchStageInfo, fetchStages } from "../data/stages.js";
import { fetchSpacecraft, fetchSpacecraftInfo } from "../data/spacecraft.js";
import { fetchPads } from "../data/pads.js";
import Filter from "../multipurpose/Filter.js";
import { FilterIcon } from "@heroicons/react/outline";
import Card from "../informationCards/InformationCard.js";
import { fetchZones } from "../data/recoveryZones.js";
import {
  buildReuseChart,
  buildTimelineRowFromFlights,
  extractFlightDates,
  statusIsActive,
} from "../graphComponents/reuseTimeline.js";
import { FleetSnapshotStrip, ReuseTimelinePanel, TopFlyerLink } from "./fleetPanels.js";
import "./vehicleList.css";

const STAGE_STATUS_ORDER = ["ACTIVE", "RETIRED", "EXPENDED", "LOST", "EXPENDED_ACTIVE", "IN_USE"];
const VEHICLE_FILTER_ORDER = {
  spacecraft: ["type", "status", "version"],
  stage: ["rocket", "status", "version"],
};
const TIMELINE_ROW_LIMIT = 10;
const STATIC_MEDIA_HOST = "https://boostertracker-static.s3.amazonaws.com";
const STARSHIP_FALLBACK_IMAGE = "/media/rocket_pad_photos/Starship_OLPA_launches_photo.jpg";

// Stock photos used only when a vehicle has no unique image (e.g. default_booster.jpg).
// Hashed by vehicle id so the same booster keeps a stable image, while neighbors diverge.
const FALCON_STOCK_IMAGES = [
  "/media/rocket_pad_photos/Falcon_LC39A_launches_photo.jpg",
  "/media/rocket_pad_photos/Falcon_SLC40_launches_photo.jpg",
  "/media/rocket_pad_photos/Falcon_SLC4E_launches_photo.jpg",
  "/media/rocket_pad_photos/Falcon_Heavy_launches_photo.jpg",
  "/media/rocket_photos/falcon9.jpeg",
  "/media/rocket_photos/Falconheavy.jpeg",
  "/media/pad_photos/lc39a.jpeg",
  "/media/pad_photos/slc_40.jpeg",
  "/media/pad_photos/slc_4e.jpeg",
];

const STARSHIP_STOCK_IMAGES = [
  "/media/rocket_pad_photos/Starship_OLPA_launches_photo.jpg",
  "/media/rocket_photos/Starship.jpeg",
  "/media/pad_photos/IMG_4349.jpeg",
  "/media/pad_photos/olp_a_qknNfEa.jpeg",
];

const ORDER_OPTIONS = [
  { value: "-flights", label: "Most flights" },
  { value: "flights", label: "Fewest flights" },
  { value: "name", label: "Name A–Z" },
  { value: "-name", label: "Name Z–A" },
  { value: "status", label: "Status" },
];

const getRocketName = (rocketId, rockets) =>
  rockets.find((rocket) => String(rocket.id) === String(rocketId))?.name || "Unknown";

const formatFilterValue = (filterKey, value, rockets) => {
  if (filterKey === "rocket") {
    return getRocketName(value, rockets);
  }

  return versionFormat(toTitleCase(String(value || "Unknown").replace(/_/g, " "))).replace("Tbd", "TBD");
};

const formatEnumValue = (value, fallback = "Unknown") =>
  toTitleCase(String(value || fallback).replace(/_/g, " ")).replace("Tbd", "TBD");

const sortFilterValues = (filterKey, values, rockets) => {
  if (filterKey === "rocket") {
    return [...values].sort((a, b) => getRocketName(a, rockets).localeCompare(getRocketName(b, rockets)));
  }

  if (filterKey === "status") {
    return [...values].sort((a, b) => {
      const aIndex = STAGE_STATUS_ORDER.indexOf(a);
      const bIndex = STAGE_STATUS_ORDER.indexOf(b);
      return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
    });
  }

  if (filterKey === "version") {
    return [...values].sort((a, b) => b.localeCompare(a, undefined, { numeric: true }));
  }

  return values;
};

const isFlatFilterGroup = (value) =>
  value && typeof value === "object" && Object.values(value).every((option) => typeof option === "boolean");

const getVehicleFilterKeys = (filter, type) => {
  const keys = Object.keys(filter || {}).filter((key) => isFlatFilterGroup(filter[key]));
  const preferredOrder = VEHICLE_FILTER_ORDER[type] || [];

  return keys.sort((a, b) => {
    const aIndex = preferredOrder.indexOf(a);
    const bIndex = preferredOrder.indexOf(b);

    if (aIndex !== -1 || bIndex !== -1) {
      return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
    }

    return a.localeCompare(b);
  });
};

const getSearchLabel = (type, stageType) => {
  if (type === "spacecraft") {
    return "Search spacecraft";
  }

  if (type === "stage" && stageType === "SECOND_STAGE") {
    return "Search ships";
  }

  return "Search boosters";
};

const getPageCopy = (type, stageType, family) => {
  const familyKey = String(family || "").toLowerCase();

  if (type === "spacecraft") {
    return {
      kicker: "Dragon fleet",
      title: "Dragons",
      blurb: "Browse Crew and Cargo Dragon capsules by status, version, and flight count.",
      timelineLabel: "Capsule reuse timeline",
      timelineTitle: "Flights since debut",
      vehicleNoun: "capsules",
      detailPath: "spacecraft",
    };
  }

  if (type === "stage" && stageType === "SECOND_STAGE") {
    return {
      kicker: familyKey === "starship" ? "Starship fleet" : "Fleet",
      title: "Ships",
      blurb: "Track Starship upper stages and their flight history.",
      timelineLabel: "Ship reuse timeline",
      timelineTitle: "Flights since debut",
      vehicleNoun: "ships",
      detailPath: "stage",
    };
  }

  if (familyKey === "starship") {
    return {
      kicker: "Starship fleet",
      title: "Boosters",
      blurb: "Super Heavy boosters and their early reuse record.",
      timelineLabel: "Booster reuse timeline",
      timelineTitle: "Flights since debut",
      vehicleNoun: "boosters",
      detailPath: "stage",
    };
  }

  return {
    kicker: "Falcon fleet",
    title: "Boosters",
    blurb: "Search and filter Falcon 9 and Heavy boosters by status, version, and flight count.",
    timelineLabel: "Booster reuse timeline",
    timelineTitle: "Flights since debut",
    vehicleNoun: "boosters",
    detailPath: "stage",
  };
};

const parseStartFilterValue = (value) => {
  if (value && typeof value === "object") {
    return value;
  }

  if (typeof value !== "string") {
    return value;
  }

  try {
    return JSON.parse(value.replace(/'/g, '"').replace(/True/g, "true").replace(/False/g, "false"));
  } catch (error) {
    return value;
  }
};

/** Falcon boosters: timeline + snapshot live on /falcon/booster-stats/ so search results stay above the fold. */
const shouldShowFleetPanels = (type, stageType, family) => {
  if (type === "stage" && stageType === "BOOSTER" && String(family || "").toLowerCase() === "falcon") {
    return false;
  }
  return type === "stage" || type === "spacecraft";
};

const cleanImagePath = (image) => {
  if (!image) {
    return "";
  }

  return String(image)
    .replace(/^http:\/\/127\.0\.0\.1:8000\/?/, "/")
    .replace(/^https?:\/\/boostertracker-static\.s3\.amazonaws\.com/i, "");
};

const getImageUrl = (image) => {
  const imagePath = cleanImagePath(image);

  if (!imagePath) {
    return "";
  }

  if (/^https?:\/\//.test(imagePath)) {
    return imagePath;
  }

  const mediaPath = imagePath.startsWith("/") ? imagePath : `/${imagePath}`;

  // Relative /media paths 404 on the SPA host in production — serve from S3.
  if (mediaPath.startsWith("/media/")) {
    if (typeof window !== "undefined" && window.location.hostname === "localhost") {
      return `${getPhotoLocation()}${mediaPath}`;
    }
    return `${STATIC_MEDIA_HOST}${mediaPath}`;
  }

  return `${getPhotoLocation()}${mediaPath}`;
};

const isGenericStageImage = (image) =>
  !image || /\/media\/stage_photos\/default_booster\.jpg$/i.test(cleanImagePath(image));

const hashString = (value) => {
  const str = String(value || "");
  let hash = 0;

  for (let index = 0; index < str.length; index += 1) {
    hash = (hash * 31 + str.charCodeAt(index)) >>> 0;
  }

  return hash;
};

const pickStockImage = (vehicle, rocket) => {
  const isStarship = rocket?.name === "Starship";
  // Family-scoped pools only — Falcon never draws from Starship art and vice versa.
  const pool = isStarship ? STARSHIP_STOCK_IMAGES : FALCON_STOCK_IMAGES;
  const rocketImage = rocket?.image ? cleanImagePath(rocket.image) : "";
  // Only prepend this vehicle's own rocket image when it matches the same family pool.
  const options =
    rocketImage && !pool.includes(rocketImage) && (isStarship || !/starship/i.test(rocketImage))
      ? [rocketImage, ...pool]
      : [...pool];
  const index = hashString(vehicle?.id ?? vehicle?.name) % Math.max(options.length, 1);

  return options[index] || pool[0];
};

/**
 * Resolve a vehicle thumb URL.
 * Unique stage/spacecraft photos are always kept. Generic defaults (missing image or
 * default_booster.jpg) are mapped to a stable stock photo so adjacent cards don't
 * all show the same placeholder.
 */
const getVehicleImageUrl = (vehicle, type, rockets) => {
  const rocket = rockets.find((item) => String(item.id) === String(vehicle?.rocket));
  const hasUniqueImage = Boolean(vehicle?.image) && !isGenericStageImage(vehicle.image);

  // Keep real booster / capsule photos as-is.
  if (hasUniqueImage) {
    return getImageUrl(vehicle.image);
  }

  if (type === "stage") {
    return getImageUrl(pickStockImage(vehicle, rocket));
  }

  // Spacecraft / other: fall back to rocket art, then diversified stock.
  return getImageUrl(rocket?.image) || getImageUrl(pickStockImage(vehicle, rocket));
};

const getStatusClass = (status) => {
  const normalized = String(status || "").toUpperCase();

  if (statusIsActive(normalized)) {
    return "active";
  }

  if (normalized.includes("RETIRED")) {
    return "retired";
  }

  if (normalized.includes("EXPENDED")) {
    return "expended";
  }

  if (normalized.includes("LOST")) {
    return "lost";
  }

  return "unknown";
};

const sortVehicles = (vehicles, orderBy) => {
  const list = [...vehicles];

  const byFlights = (a, b) => Number(b.num_launches || 0) - Number(a.num_launches || 0);
  const byName = (a, b) => String(a.name || "").localeCompare(String(b.name || ""), undefined, { numeric: true });
  const byStatus = (a, b) => {
    const aIndex = STAGE_STATUS_ORDER.indexOf(String(a.status || "").toUpperCase());
    const bIndex = STAGE_STATUS_ORDER.indexOf(String(b.status || "").toUpperCase());
    const statusDelta = (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
    return statusDelta || byFlights(a, b);
  };

  switch (orderBy) {
    case "flights":
      return list.sort((a, b) => Number(a.num_launches || 0) - Number(b.num_launches || 0) || byName(a, b));
    case "name":
      return list.sort(byName);
    case "-name":
      return list.sort((a, b) => byName(b, a));
    case "status":
      return list.sort(byStatus);
    case "-flights":
    default:
      return list.sort((a, b) => byFlights(a, b) || byName(a, b));
  }
};

const VehicleFilterGroup = ({ filterKey, filter, label, rockets, isVisible, onSelectAll, onToggle }) => {
  if (!isVisible) {
    return null;
  }

  const options = sortFilterValues(filterKey, Object.keys(filter?.[filterKey] || {}), rockets);

  if (!options.length) {
    return null;
  }

  const selectedCount = options.filter((option) => filter?.[filterKey]?.[option]).length;
  const allSelected = selectedCount === options.length;

  return (
    <section className="vl-filter-group">
      <div className="vl-filter-group-head">
        <span>{label}</span>
        <small>
          {selectedCount}/{options.length}
        </small>
      </div>
      <div className="vl-filter-options">
        <button
          className={`vl-filter-chip vl-filter-all ${allSelected ? "selected" : ""}`}
          onClick={() => onSelectAll(filterKey)}
          type="button"
        >
          All
        </button>
        {options.map((option) => {
          const selected = Boolean(filter?.[filterKey]?.[option]);

          return (
            <button
              className={`vl-filter-chip ${selected ? "selected" : ""}`}
              key={`${filterKey}-${option}`}
              onClick={() => onToggle(filterKey, option)}
              type="button"
            >
              {formatFilterValue(filterKey, option, rockets)}
            </button>
          );
        })}
      </div>
    </section>
  );
};

const VehiclePagination = ({ currentPage, totalPages, onPageChange }) => (
  <div className="vl-pagination" aria-label="Pagination">
    <button
      className="vl-icon-button"
      onClick={() => onPageChange(-currentPage + 1)}
      disabled={currentPage === 1}
      aria-label="First page"
      type="button"
    >
      «
    </button>
    <button
      className="vl-icon-button"
      onClick={() => onPageChange(-1)}
      disabled={currentPage === 1}
      aria-label="Previous page"
      type="button"
    >
      ‹
    </button>
    <span>
      Page {currentPage} / {totalPages}
    </span>
    <button
      className="vl-icon-button"
      onClick={() => onPageChange(1)}
      disabled={currentPage >= totalPages}
      aria-label="Next page"
      type="button"
    >
      ›
    </button>
    <button
      className="vl-icon-button"
      onClick={() => onPageChange(totalPages - currentPage)}
      disabled={currentPage >= totalPages}
      aria-label="Last page"
      type="button"
    >
      »
    </button>
  </div>
);

const VehicleListCard = ({ vehicle, type, rockets, maxFlights, detailPath }) => {
  const rocketName = type === "stage" ? getRocketName(vehicle.rocket, rockets) : null;
  const statusLabel = formatEnumValue(vehicle.status);
  const statusClass = getStatusClass(vehicle.status);
  const flights = Number(vehicle.num_launches || 0);
  const barWidth = maxFlights > 0 ? Math.max(6, (flights / maxFlights) * 100) : 0;
  const imageUrl = getVehicleImageUrl(vehicle, type, rockets);
  const typeLabel =
    type === "spacecraft"
      ? formatEnumValue(vehicle.type)
      : formatEnumValue(vehicle.type).replace("Booster", "Booster").replace("Second Stage", "Ship");

  return (
    <Link to={`/${detailPath}/${vehicle.id}/`} className="vl-vehicle-link">
      <article className="vl-vehicle-card">
        <span
          className="vl-vehicle-thumb"
          style={{ backgroundImage: imageUrl ? `url(${imageUrl})` : undefined }}
          aria-hidden="true"
        />
        <span className="vl-vehicle-main">
          <span className="vl-vehicle-kicker">
            <span className={`vl-status-pill ${statusClass}`}>{statusLabel}</span>
            {vehicle.version ? <span>{versionFormat(vehicle.version)}</span> : null}
          </span>
          <strong className="vl-vehicle-title">{vehicle.name}</strong>
          {vehicle.nickname ? <span className="vl-vehicle-nickname">{vehicle.nickname}</span> : null}
          <span className="vl-vehicle-meta">
            {rocketName ? (
              <span className="vl-vehicle-fact">
                <small>Rocket</small>
                <strong>{rocketName}</strong>
              </span>
            ) : (
              <span className="vl-vehicle-fact">
                <small>Type</small>
                <strong>{typeLabel}</strong>
              </span>
            )}
            <span className="vl-vehicle-fact">
              <small>Version</small>
              <strong>{vehicle.version ? versionFormat(vehicle.version) : "—"}</strong>
            </span>
            <span className="vl-vehicle-fact is-sort-key">
              <small>Flights</small>
              <strong>x{flights}</strong>
            </span>
          </span>
          <span className="vl-flight-bar" aria-hidden="true">
            <i style={{ width: `${barWidth}%` }} />
          </span>
        </span>
      </article>
    </Link>
  );
};

const VehicleList = ({ type, familyID }) => {
  const initFromURL = (param, defaultValue = "") => {
    const params = new URLSearchParams(window.location.search);
    return params.get(param) || defaultValue;
  };

  const initFilterFromURL = () => {
    const filterParam = initFromURL("filter");
    return filterParam ? JSON.parse(decodeURIComponent(filterParam)) : null;
  };

  const [query, setQuery] = useState(initFromURL("query"));
  const [inputValue, setInputValue] = useState(initFromURL("query"));
  const [filter, setFilter] = useState(initFilterFromURL());
  const [currentPage, setCurrentPage] = useState(Number(initFromURL("page", 1)));
  const [orderBy, setOrderBy] = useState(initFromURL("order", "-flights"));
  const [numVehicles, setNumVehicles] = useState(0);
  const [rockets, setRockets] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [fleetVehicles, setFleetVehicles] = useState([]);
  const [isFilterVisible, setIsFilterVisible] = useState(window.innerWidth >= 768);
  const [loading, setLoading] = useState(false);
  const [stageType] = useState(window.location.href.includes("boosters") ? "BOOSTER" : "SECOND_STAGE");
  const [reuseTimeline, setReuseTimeline] = useState([]);
  const [reuseLoading, setReuseLoading] = useState(false);
  const [reuseView, setReuseView] = useState("relative");
  const isVehicleFilterBrowser = type === "stage" || type === "spacecraft";
  const initLoad = useRef(filter ? false : true);
  const timelineCacheKey = useRef("");

  const vehiclesPerPage = 25;
  const debouncedFilter = useDebounce(filter, 0);
  const family = window.location.pathname.split("/")[1];
  const pageCopy = getPageCopy(type, stageType, family);
  const showFleetPanels = shouldShowFleetPanels(type, stageType, family);

  const objectsData = { rocket: rockets };
  const filterNames = { version: "Version", rocket: "Rocket", status: "Status", type: "Type" };

  const updateFilter = useCallback((newFilter) => {
    setFilter(newFilter);
    setCurrentPage(1);
  }, []);

  const updateURL = useCallback(() => {
    const params = new URLSearchParams();
    params.set("filter", encodeURIComponent(JSON.stringify(debouncedFilter)));
    params.set("page", currentPage);
    params.set("query", query);
    if (isVehicleFilterBrowser) {
      params.set("order", orderBy);
    }
    window.history.replaceState({}, "", `${window.location.pathname}?${params}`);
  }, [debouncedFilter, currentPage, query, orderBy, isVehicleFilterBrowser]);

  useEffect(() => {
    updateURL();
  }, [query, currentPage, debouncedFilter, orderBy, updateURL]);

  // Fetch list data
  useEffect(() => {
    (async () => {
      if (debouncedFilter && initLoad.current) {
        initLoad.current = false;
        return;
      }
      setLoading(true);

      let vehicleData = [];
      let pathFamily = window.location.pathname.split("/")[1];
      let startFilter = null;

      if (type === "stage") {
        const stageData = await fetchStages(query, debouncedFilter, pathFamily, stageType);
        const rocketData = await fetchRockets();

        startFilter = stageData["start_filter"];
        vehicleData = stageData["stages"];
        setRockets(rocketData);
      } else if (type === "spacecraft") {
        const spacecraftData = await fetchSpacecraft(query, debouncedFilter, pathFamily);
        startFilter = spacecraftData["start_filter"];
        vehicleData = spacecraftData["spacecraft"];
      } else if (type === "pad") {
        vehicleData = await fetchPads(query, debouncedFilter);
      } else if (type === "recoveryzone") {
        vehicleData = await fetchZones(query, debouncedFilter);
      }

      if (!debouncedFilter && startFilter) {
        const parsedFilter = {};
        for (const key of Object.keys(startFilter)) {
          parsedFilter[key] = parseStartFilterValue(startFilter[key]);
        }
        setFilter(parsedFilter);
      }
      setVehicles(vehicleData || []);
      setNumVehicles((vehicleData || []).length);
      setLoading(false);
    })();
  }, [query, debouncedFilter, type, familyID, stageType]);

  // Fleet baseline for timeline (unfiltered family) — load once per type/family/stageType
  useEffect(() => {
    if (!showFleetPanels) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const pathFamily = window.location.pathname.split("/")[1];
        let fleet = [];

        if (type === "stage") {
          const stageData = await fetchStages("", null, pathFamily, stageType);
          fleet = stageData.stages || [];
        } else {
          const spacecraftData = await fetchSpacecraft("", null, pathFamily);
          fleet = spacecraftData.spacecraft || [];
        }

        if (!cancelled) {
          setFleetVehicles(fleet);
        }
      } catch (error) {
        if (!cancelled) {
          setFleetVehicles([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [type, stageType, familyID, showFleetPanels]);

  // Load reuse timeline from top fleet vehicles (cached by fleet composition)
  useEffect(() => {
    if (!showFleetPanels || !fleetVehicles.length) {
      return undefined;
    }

    const topVehicles = [...fleetVehicles]
      .filter((vehicle) => Number(vehicle.num_launches) > 0)
      .sort((a, b) => Number(b.num_launches || 0) - Number(a.num_launches || 0))
      .slice(0, TIMELINE_ROW_LIMIT);

    const cacheKey = `${type}:${stageType}:${familyID}:${topVehicles.map((v) => v.id).join(",")}`;
    if (cacheKey === timelineCacheKey.current) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      setReuseLoading(true);
      try {
        const details = await Promise.all(
          topVehicles.map(async (vehicle) => {
            try {
              if (type === "stage") {
                const detail = await fetchStageInfo(vehicle.id);
                const flights = extractFlightDates(detail.stage_and_recoveries);
                return buildTimelineRowFromFlights({
                  id: vehicle.id,
                  name: vehicle.name,
                  active: statusIsActive(vehicle.status),
                  flights,
                  count: Number(vehicle.num_launches || flights.length),
                });
              }

              const detail = await fetchSpacecraftInfo(vehicle.id);
              const flights = extractFlightDates(detail.spacecraft_on_launches);
              return buildTimelineRowFromFlights({
                id: vehicle.id,
                name: vehicle.name,
                active: statusIsActive(vehicle.status),
                flights,
                count: Number(vehicle.num_launches || flights.length),
              });
            } catch (error) {
              return null;
            }
          })
        );

        if (!cancelled) {
          timelineCacheKey.current = cacheKey;
          setReuseTimeline(details.filter(Boolean));
        }
      } catch (error) {
        if (!cancelled) {
          setReuseTimeline([]);
        }
      } finally {
        if (!cancelled) {
          setReuseLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fleetVehicles, type, stageType, familyID, showFleetPanels]);

  const sortedVehicles = useMemo(
    () => (isVehicleFilterBrowser ? sortVehicles(vehicles, orderBy) : vehicles),
    [vehicles, orderBy, isVehicleFilterBrowser]
  );

  const totalPages = Math.ceil(numVehicles / vehiclesPerPage);
  const safeTotalPages = Math.max(1, totalPages);

  const handlePageChange = useCallback(
    (delta) => {
      setCurrentPage((prev) => Math.max(1, Math.min(prev + delta, safeTotalPages)));
    },
    [safeTotalPages]
  );

  const handleSearch = useCallback(() => {
    setQuery(inputValue);
    setCurrentPage(1);
  }, [inputValue]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        handleSearch();
      }
    },
    [handleSearch]
  );

  const toggleFilterVisibility = useCallback(() => {
    setIsFilterVisible((prev) => !prev);
  }, []);

  const resetFilters = () => {
    const newFilter = filter ? setAllValuesTrue(filter) : filter;
    setFilter(newFilter);
    setQuery("");
    setInputValue("");
    setCurrentPage(1);
    setOrderBy("-flights");
  };

  const commitVehicleFilter = useCallback(
    (newFilter) => {
      setFilter(newFilter);
      const params = new URLSearchParams(window.location.search);
      params.set("filter", encodeURIComponent(JSON.stringify(newFilter)));
      params.set("page", 1);
      params.set("query", query);
      params.set("order", orderBy);
      window.history.replaceState({}, "", `${window.location.pathname}?${params}`);
    },
    [query, orderBy]
  );

  const toggleVehicleFilter = useCallback(
    (filterKey, option) => {
      setFilter((currentFilter) => {
        const newFilter = {
          ...currentFilter,
          [filterKey]: {
            ...(currentFilter?.[filterKey] || {}),
            [option]: !currentFilter?.[filterKey]?.[option],
          },
        };
        const params = new URLSearchParams(window.location.search);
        params.set("filter", encodeURIComponent(JSON.stringify(newFilter)));
        params.set("page", 1);
        params.set("query", query);
        params.set("order", orderBy);
        window.history.replaceState({}, "", `${window.location.pathname}?${params}`);
        return newFilter;
      });
      setCurrentPage(1);
    },
    [query, orderBy]
  );

  const selectAllVehicleFilter = useCallback(
    (filterKey) => {
      const newFilter = {
        ...filter,
        [filterKey]: Object.fromEntries(Object.keys(filter?.[filterKey] || {}).map((key) => [key, true])),
      };
      commitVehicleFilter(newFilter);
      setCurrentPage(1);
    },
    [commitVehicleFilter, filter]
  );

  const paginatedVehicles = sortedVehicles.slice((currentPage - 1) * vehiclesPerPage, currentPage * vehiclesPerPage);
  const vehicleFilterKeys = getVehicleFilterKeys(filter, type);
  const reuseChart = useMemo(() => buildReuseChart(reuseTimeline, reuseView), [reuseTimeline, reuseView]);

  const summaryStats = useMemo(() => {
    const activeCount = vehicles.filter((vehicle) => statusIsActive(vehicle.status)).length;
    const totalFlights = vehicles.reduce((sum, vehicle) => sum + Number(vehicle.num_launches || 0), 0);
    const topFlyer = [...vehicles].sort((a, b) => Number(b.num_launches || 0) - Number(a.num_launches || 0))[0];

    return {
      total: vehicles.length,
      active: activeCount,
      flights: totalFlights,
      topFlyer: topFlyer && Number(topFlyer.num_launches || 0) > 0 ? topFlyer : null,
    };
  }, [vehicles]);

  const maxFlights = useMemo(
    () => Math.max(1, ...sortedVehicles.map((vehicle) => Number(vehicle.num_launches || 0)), 1),
    [sortedVehicles]
  );

  // Legacy layout for pads / recovery zones
  if (!isVehicleFilterBrowser) {
    return (
      <div className="max-w-6xl mx-auto page-container">
        <div className="left-container">
          <div className="filter-container">
            <input
              type="text"
              name="q"
              placeholder="Search"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="search-input"
            />
            <div className="vl-action-row">
              <button onClick={handleSearch} className="search-button">
                Search
              </button>
              <button onClick={resetFilters} className="clear-button">
                Clear
              </button>
            </div>
          </div>
          <div className="pagination-container">
            <h2 className="text-xl mb-2 bold">
              Page {currentPage} of {safeTotalPages}
            </h2>
            <div className="pagination-buttons-filter">
              <div className="pagination-buttons">
                <button
                  className="pagination-button"
                  onClick={() => handlePageChange(-currentPage + 1)}
                  disabled={currentPage === 1}
                >
                  «
                </button>
                <button className="pagination-button" onClick={() => handlePageChange(-1)} disabled={currentPage === 1}>
                  ‹
                </button>
                <button
                  className="pagination-button"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage >= totalPages}
                >
                  ›
                </button>
                <button
                  className="pagination-button"
                  onClick={() => handlePageChange(totalPages - currentPage)}
                  disabled={currentPage >= totalPages}
                >
                  »
                </button>
              </div>
              <button className="filter-toggle-button" onClick={toggleFilterVisibility}>
                <FilterIcon className="h-6 w-6 text-gray-300" />
              </button>
            </div>
          </div>
          <div className="filter-width lg:w-1/4 lg:mr-4 lg:mt-0">
            <Filter
              filter={filter}
              updateFilter={updateFilter}
              filterNames={filterNames}
              objects={objectsData}
              isVisible={isFilterVisible}
            />
          </div>
        </div>
        <div className="launch-container">
          <div className="launch-cards-container">
            {loading ? (
              <div className="loading-indicator"></div>
            ) : paginatedVehicles.length > 0 ? (
              paginatedVehicles.map((vehicle) => (
                <Card
                  key={vehicle.id}
                  object={vehicle}
                  objectType={type}
                  subObjects={{ rockets }}
                  vehicleType={type}
                  additionalInfo={{}}
                />
              ))
            ) : (
              <p>No vehicles are available.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="vl-browser-page">
      <section className="vl-hero">
        <div>
          <p className="bt-section-label">{pageCopy.kicker}</p>
          <h1>{pageCopy.title}</h1>
          <p>{pageCopy.blurb}</p>
        </div>
        <div className="vl-summary-grid">
          <span>
            <small>Total</small>
            <strong>{numberWithCommas(summaryStats.total)}</strong>
          </span>
          <span>
            <small>Active</small>
            <strong>{numberWithCommas(summaryStats.active)}</strong>
          </span>
          <span>
            <small>Flights</small>
            <strong>{numberWithCommas(summaryStats.flights)}</strong>
          </span>
          <span>
            <small>Top flyer</small>
            <TopFlyerLink vehicle={summaryStats.topFlyer} detailPath={pageCopy.detailPath} />
          </span>
        </div>
      </section>

      <div className="vl-browser-layout">
        <aside className="vl-command-panel">
          <div className="vl-toolbar">
            <label className="vl-search-field">
              <span>{getSearchLabel(type, stageType)}</span>
              <input
                type="text"
                name="q"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={type === "spacecraft" ? "C208, Endeavour…" : "B1067, B1071…"}
              />
            </label>

            <label className="vl-sort-field">
              <span>Order by</span>
              <select value={orderBy} onChange={(e) => { setOrderBy(e.target.value); setCurrentPage(1); }}>
                {ORDER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <div className="vl-action-row">
              <button onClick={handleSearch} className="vl-action-button primary" type="button">
                Search
              </button>
              <button onClick={resetFilters} className="vl-action-button" type="button">
                Clear
              </button>
            </div>

            <VehiclePagination currentPage={currentPage} totalPages={safeTotalPages} onPageChange={handlePageChange} />
          </div>

          <button
            className={`vl-mobile-filter-toggle ${isFilterVisible ? "open" : ""}`}
            onClick={toggleFilterVisibility}
            type="button"
            aria-expanded={isFilterVisible}
          >
            <FilterIcon aria-hidden="true" />
            <span>{isFilterVisible ? "Hide filters" : "Show filters"}</span>
          </button>

          <div className={`vl-filter-groups ${isFilterVisible ? "show" : ""}`}>
            {vehicleFilterKeys.map((filterKey) => (
              <VehicleFilterGroup
                filterKey={filterKey}
                filter={filter}
                key={filterKey}
                label={filterNames[filterKey] || formatFilterValue("", filterKey, rockets)}
                rockets={rockets}
                isVisible
                onSelectAll={selectAllVehicleFilter}
                onToggle={toggleVehicleFilter}
              />
            ))}
          </div>
        </aside>

        <div className="vl-main-column">
          {showFleetPanels ? (
            <>
              <ReuseTimelinePanel
                copy={pageCopy}
                reuseChart={reuseChart}
                reuseView={reuseView}
                setReuseView={setReuseView}
                loading={reuseLoading && !reuseTimeline.length}
                detailPath={pageCopy.detailPath}
              />

              <FleetSnapshotStrip vehicles={vehicles.length ? vehicles : fleetVehicles} copy={pageCopy} />
            </>
          ) : null}

          <section className="vl-results-panel">
            <div className="vl-results-head">
              <div>
                <p className="bt-section-label">Directory</p>
                <h2>
                  {numberWithCommas(numVehicles)} {pageCopy.vehicleNoun}
                </h2>
              </div>
              <span className="vl-results-meta">
                Showing {paginatedVehicles.length} · page {currentPage}/{safeTotalPages}
              </span>
            </div>

            <div className={`vl-vehicle-feed ${loading ? "is-updating" : ""}`}>
              {loading ? (
                <div className="vl-reuse-loading">
                  <span className="loading-indicator" />
                </div>
              ) : paginatedVehicles.length > 0 ? (
                paginatedVehicles.map((vehicle) => (
                  <VehicleListCard
                    key={vehicle.id}
                    vehicle={vehicle}
                    type={type}
                    rockets={rockets}
                    maxFlights={maxFlights}
                    detailPath={pageCopy.detailPath}
                  />
                ))
              ) : (
                <div className="vl-empty-results">
                  <strong>No matching {pageCopy.vehicleNoun}</strong>
                  <span>Try clearing filters or adjusting your search.</span>
                </div>
              )}
            </div>

            <div className="vl-results-foot">
              <VehiclePagination currentPage={currentPage} totalPages={safeTotalPages} onPageChange={handlePageChange} />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default VehicleList;
